import React from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import { FulfillmentStepKind, InternalConfirmationState } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto, OrderInternalConfirmationDto } from "client/api-v2";
import { detailFieldLabelSx } from "components/InfoField/InfoField";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { isOrderLocked } from "areas/orders/builder/LockedOrderBanner";
import type { SectionProps } from "areas/orders/builder/sections/SectionProps";
import { formatInstantInUtc } from "areas/orders/components/OrderVersionHistory";
import { setInternalConfirmation } from "areas/orders/services/orderService";

const nothingTriggersReview = "Nothing on this order triggers internal review.";

const canGoToTheCustomer = "It can go straight to the customer.";

const advisoryStatement = "Flagged for review. This is advisory only — it does not block publishing or acceptance.";

const gatingStatement = "Staff must confirm this order before billing setup or provisioning can start.";

function releasedStatement(hold: OrderInternalConfirmationDto): string {
  const when = hold.confirmedAt === null ? "an unrecorded time" : `${formatInstantInUtc(hold.confirmedAt)} UTC`;
  const released = hold.confirmedByUser
    ? `Confirmed by ${hold.confirmedByUser} at ${when}.`
    : `Confirmed at ${when}, by a staff member the record does not name.`;
  return `${released} Billing setup and provisioning can start.`;
}

export const manualFulfilmentHoldReason = "Automatic fulfillment is off for this order.";

export const manualFulfilmentStatement =
  "A staff member must approve fulfillment before billing setup or provisioning can start.";

export const manualFulfilmentApprovedStatement =
  "A staff member approved fulfillment. Billing setup and provisioning can start.";

const advisoryHeading = "Advisory review";
const gatingHeading = "Staff confirmation hold";
export const manualFulfilmentHeading = "Fulfillment approval hold";

function ReasonRow({
  reason,
  accent,
  icon,
}: {
  reason: string;
  accent: "warning.main" | "info.main";
  icon: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "flex-start",
        border: 1,
        borderColor: "divider",
        borderLeft: 4,
        borderLeftColor: accent,
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <Box sx={{ color: accent, display: "flex", flex: "none" }}>{icon}</Box>
      <Typography variant="body2">{reason}</Typography>
    </Stack>
  );
}

function ReviewGroup({ heading, testId, children }: { heading: string; testId: string; children: React.ReactNode }) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }} data-testid={testId}>
      <Stack spacing={1.5}>
        <Typography variant="body2" component="h3" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
          {heading}
        </Typography>
        {children}
      </Stack>
    </Box>
  );
}

function HoldReadOnly({ hold, isReleased }: { hold: OrderInternalConfirmationDto; isReleased: boolean }) {
  const state = isReleased ? "Released" : hold.isRequired ? "Required before billing setup can start" : "Not required";
  return (
    <Box>
      <Typography variant="body2">{`Staff confirmation: ${state}`}</Typography>
      {hold.note && (
        <Typography variant="body2" color="text.secondary">
          {hold.note}
        </Typography>
      )}
    </Box>
  );
}

function isManualGateComplete(fulfillment?: OrderFulfillmentDto): boolean {
  return (fulfillment?.steps ?? []).some((step) => step.kind === FulfillmentStepKind.ManualSignOff && step.isComplete);
}

export function InternalReviewSection({
  order,
  draft,
  readOnly,
  fulfillment,
  onOrderChanged,
}: SectionProps & {
  fulfillment?: OrderFulfillmentDto;
  onOrderChanged: (order: OrderDto) => void;
}) {
  const [savedHold, setSavedHold] = React.useState(order.internalConfirmation);
  const [isRequired, setIsRequired] = React.useState(savedHold.isRequired);
  const [note, setNote] = React.useState(savedHold.note ?? "");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<unknown>(undefined);

  const advisoryReasons = order.reviewReasons;
  const manualFulfilmentApplies = draft.requiresManualFulfillmentApproval;
  const gatingReasons = manualFulfilmentApplies
    ? [...order.internalConfirmationReasons, manualFulfilmentHoldReason]
    : order.internalConfirmationReasons;
  const confirmationApplies = order.internalConfirmationReasons.length > 0 || savedHold.isRequired;
  const gatingApplies = confirmationApplies || manualFulfilmentApplies;
  const isReleased = savedHold.state === InternalConfirmationState.Confirmed;
  const isFulfilmentApproved = isManualGateComplete(fulfillment);
  const heading = confirmationApplies || !readOnly ? gatingHeading : manualFulfilmentHeading;
  const nothingFires = advisoryReasons.length === 0 && !gatingApplies;
  const isHoldDirty = isRequired !== savedHold.isRequired || note !== (savedHold.note ?? "");

  const saveHold = async () => {
    setSaving(true);
    setError(undefined);
    try {
      const saved = await setInternalConfirmation(order.odNumber, {
        requiresInternalConfirmation: isRequired,
        note: isRequired ? note.trim() || null : null,
      });
      setSavedHold(saved.internalConfirmation);
      setNote(saved.internalConfirmation.note ?? "");
      onOrderChanged(saved);
    } catch (saveError) {
      setError(saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BuilderSection title="Internal review">
      {nothingFires && (
        <Typography variant="body2" color="text.secondary">
          {isOrderLocked(order.status) ? nothingTriggersReview : `${nothingTriggersReview} ${canGoToTheCustomer}`}
        </Typography>
      )}

      {advisoryReasons.length > 0 && (
        <ReviewGroup heading={advisoryHeading} testId="advisory-review-group">
          <Alert severity="warning">{advisoryStatement}</Alert>
          <Stack spacing={1}>
            {advisoryReasons.map((reason) => (
              <ReasonRow
                key={reason}
                reason={reason}
                accent="warning.main"
                icon={<WarningAmberIcon fontSize="small" />}
              />
            ))}
          </Stack>
        </ReviewGroup>
      )}

      {(gatingApplies || !readOnly) && (
        <ReviewGroup heading={heading} testId="gating-review-group">
          {isReleased ? (
            <Alert severity="success">{releasedStatement(savedHold)}</Alert>
          ) : (
            confirmationApplies && <Alert severity="info">{gatingStatement}</Alert>
          )}
          {manualFulfilmentApplies &&
            (isFulfilmentApproved ? (
              <Alert severity="success">{manualFulfilmentApprovedStatement}</Alert>
            ) : (
              <Alert severity="info">{manualFulfilmentStatement}</Alert>
            ))}
          {gatingReasons.length > 0 && (
            <Stack spacing={1}>
              {gatingReasons.map((reason) => (
                <ReasonRow
                  key={reason}
                  reason={reason}
                  accent="info.main"
                  icon={<LockOutlinedIcon fontSize="small" />}
                />
              ))}
            </Stack>
          )}

          {readOnly ? (
            <HoldReadOnly hold={savedHold} isReleased={isReleased} />
          ) : (
            <Stack spacing={1}>
              {!!error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
              <FormControlLabel
                control={
                  <Switch
                    checked={isRequired}
                    onChange={(event) => setIsRequired(event.target.checked)}
                    slotProps={{ input: { "aria-label": "Requires internal confirmation" } }}
                  />
                }
                label="Require staff confirmation"
                slotProps={{ typography: { variant: "body2" } }}
              />
              {isRequired && (
                <TextField
                  label="Note for the confirming staff member"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              )}
              <Box>
                <Button
                  variant="outlined"
                  disabled={!isHoldDirty || saving}
                  loading={saving}
                  onClick={() => void saveHold()}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          )}
        </ReviewGroup>
      )}
    </BuilderSection>
  );
}
